import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pill, cycleText, fmtWhen, money, num } from "./cdrMeta";

const TRIGGER = { auto: "Monthly schedule", test: "Test to self", manual: "Sent by hand" };

function SendDetail({ id, onClose }) {
  const [run, setRun] = useState(null);
  useEffect(() => { setRun(null); if (id) api.get(`/cdr-dispatch/runs/${id}`).then(({ data }) => setRun(data)).catch(() => onClose()); }, [id, onClose]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="cdr-run-dialog">
        <DialogHeader><DialogTitle>{run ? cycleText(run.cycle) : "Send"}</DialogTitle></DialogHeader>
        {!run ? <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-slate-300" /> : (
          <div className="max-h-[60vh] overflow-auto">
            {run.error && <p className="mb-2 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800">{run.error}</p>}
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-3 font-semibold">Recipient</th><th className="px-3 py-2 font-semibold">To</th><th className="px-3 py-2 text-right font-semibold">Calls</th>
                <th className="px-3 py-2 text-right font-semibold">Amount</th><th className="px-3 py-2 font-semibold">File</th><th className="px-3 py-2 font-semibold">Status</th></tr></thead>
              <tbody>{(run.results || []).map((r) => (
                <tr key={r.recipient_id} className="border-b border-slate-50 align-top" data-testid={`cdr-result-${r.recipient_id}`}>
                  <td className="py-1.5 pr-3 font-medium text-slate-800">{r.name}</td><td className="px-3 py-1.5 text-slate-600">{r.to.join(", ")}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{num(r.records)}</td><td className="px-3 py-1.5 text-right font-mono tabular-nums">{money(r.revenue)}</td>
                  <td className="px-3 py-1.5 text-slate-500">{r.delivery || "—"}</td>
                  <td className="px-3 py-1.5"><Pill s={r.status} />{r.message && <div className="mt-0.5 text-[11px] text-rose-700">{r.message}</div>}</td>
                </tr>))}</tbody>
            </table>
          </div>)}
      </DialogContent>
    </Dialog>
  );
}

/** Every send (test, by hand, monthly schedule). */
export function CdrSendHistory({ refreshKey }) {
  const [runs, setRuns] = useState(null);
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  useEffect(() => { api.get("/cdr-dispatch/runs").then(({ data }) => setRuns(data.runs)).catch(() => setRuns([])); }, [refreshKey]);
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="cdr-history">
      <div className="border-b border-slate-100 px-5 py-3"><h3 className="font-heading text-sm font-semibold text-slate-800">Sends</h3></div>
      {!runs ? <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-slate-300" /> : !runs.length ? <p className="px-5 py-6 text-sm text-slate-500" data-testid="cdr-history-empty">Nothing sent yet.</p> : (
        <table className="w-full text-xs">
          <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="px-5 py-2 font-semibold">Started</th><th className="px-3 py-2 font-semibold">Cycle</th><th className="px-3 py-2 font-semibold">Recipients</th>
            <th className="px-3 py-2 text-right font-semibold">Calls</th><th className="px-3 py-2 font-semibold">Result</th></tr></thead>
          <tbody>{runs.map((r) => (
            <tr key={r.id} onClick={() => setOpen(r.id)} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" data-testid={`cdr-run-${r.id}`}>
              <td className="px-5 py-2"><div className="font-medium text-slate-800">{fmtWhen(r.started_at)}</div><div className="text-slate-500">{TRIGGER[r.trigger] || r.trigger} · {r.started_by_name}</div></td>
              <td className="px-3 py-2 text-slate-600">{cycleText(r.cycle)}</td>
              <td className="px-3 py-2">{r.recipient_ids.length}{r.test_to && <div className="text-[11px] text-slate-500">all to {r.test_to}</div>}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{r.records != null ? num(r.records) : "—"}</td>
              <td className="px-3 py-2"><Pill s={r.status} />{r.counts && <div className="mt-0.5 text-[11px] text-slate-500">{num(r.counts.sent)} sent · {num(r.counts.paused)} held · {num(r.counts.failed)} failed</div>}
                {r.status === "running" && <div className="mt-0.5 text-[11px] text-slate-500">{r.step}</div>}</td>
            </tr>))}</tbody>
        </table>)}
      <SendDetail id={open} onClose={close} />
    </Card>
  );
}
